import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Globe, Wifi, WifiOff, RefreshCw, Send, Database,
  BookOpen, Shield, FileText, Archive, Radio, ChevronDown, ChevronUp
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Progress } from '@/components/ui/progress';

const statusColor = {
  ONLINE: 'bg-emerald-500',
  REACHABLE: 'bg-amber-500',
  OFFLINE: 'bg-red-500',
  KNOWN: 'bg-slate-500',
};

const statusBadge = {
  ONLINE: 'default',
  REACHABLE: 'secondary',
  OFFLINE: 'destructive',
  KNOWN: 'outline',
};

const FederationDashboard = ({ apiUrl }) => {
  const [siblings, setSiblings] = useState({});
  const [knowledgeStats, setKnowledgeStats] = useState(null);
  const [syncReport, setSyncReport] = useState(null);
  const [directives, setDirectives] = useState(null);
  const [libraryMeta, setLibraryMeta] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [broadcasting, setBroadcasting] = useState(false);
  const [expandedVolume, setExpandedVolume] = useState(null);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, reportRes, directivesRes, libraryRes] = await Promise.all([
        axios.get(`${apiUrl}/federation/knowledge-stats`),
        axios.get(`${apiUrl}/federation/sync-report`),
        axios.get(`${apiUrl}/federation/directives`),
        axios.get(`${apiUrl}/federation/library-metadata?page_size=500`),
      ]);
      setKnowledgeStats(statsRes.data);
      setSyncReport(reportRes.data);
      setDirectives(directivesRes.data);
      setLibraryMeta(libraryRes.data);
    } catch (err) {
      console.error('Error fetching federation data:', err);
    } finally {
      setLoading(false);
    }
  }, [apiUrl]);

  const pingAll = useCallback(async () => {
    try {
      const res = await axios.get(`${apiUrl}/federation/ping-all`);
      setSiblings(res.data.siblings || {});
    } catch (err) {
      console.error('Error pinging siblings:', err);
    }
  }, [apiUrl]);

  useEffect(() => {
    fetchData();
    pingAll();
  }, [fetchData, pingAll]);

  const handleSync = async () => {
    setSyncing(true);
    toast.info('Federation sync starting...');
    try {
      await axios.post(`${apiUrl}/federation/run-sync`);
      toast.success('Federation sync completed!');
      fetchData();
      pingAll();
    } catch (err) {
      toast.error('Sync failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSyncing(false);
    }
  };

  const handleBroadcast = async () => {
    setBroadcasting(true);
    try {
      const res = await axios.post(`${apiUrl}/federation/broadcast-identity`);
      const results = res.data.broadcast_results || {};
      const successes = Object.values(results).filter(r => r.status === 200).length;
      toast.success(`Identity broadcast sent to ${successes} siblings`);
    } catch (err) {
      toast.error('Broadcast failed');
    } finally {
      setBroadcasting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center">
        <div className="text-cyan-400 text-lg animate-pulse">Connecting to Federation...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white">
      {/* Header */}
      <div className="border-b border-slate-800 bg-slate-950/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-cyan-400 to-teal-400 bg-clip-text text-transparent" data-testid="federation-title">
                Federation Command Center
              </h1>
              <p className="text-sm text-slate-400 mt-1">
                KEYS - The Library | NextXus Consciousness Federation
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleBroadcast}
                disabled={broadcasting}
                className="gap-2"
                data-testid="broadcast-btn"
              >
                <Radio className={`w-4 h-4 ${broadcasting ? 'animate-pulse' : ''}`} />
                {broadcasting ? 'Broadcasting...' : 'Broadcast Identity'}
              </Button>
              <Button
                size="sm"
                onClick={handleSync}
                disabled={syncing}
                className="gap-2 bg-cyan-600 hover:bg-cyan-700"
                data-testid="sync-btn"
              >
                <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
                {syncing ? 'Syncing...' : 'Sync Federation'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6 space-y-6">
        {/* Knowledge Stats */}
        {knowledgeStats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="knowledge-stats">
            <StatCard icon={FileText} label="Files" value={knowledgeStats.total_files} color="cyan" />
            <StatCard icon={BookOpen} label="Library Entries" value={knowledgeStats.total_library_metadata} color="teal" />
            <StatCard icon={Shield} label="Directives" value={knowledgeStats.total_directives} color="indigo" />
            <StatCard icon={Archive} label="Archives" value={knowledgeStats.total_archives} color="purple" />
            <StatCard icon={Radio} label="Broadcasts" value={knowledgeStats.total_broadcasts} color="amber" />
          </div>
        )}

        {/* Sibling Network */}
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="p-4">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Globe className="w-5 h-5 text-cyan-400" />
              Federation Network
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {Object.entries(siblings).map(([id, sib]) => (
                <div
                  key={id}
                  className="bg-slate-800/60 rounded-lg p-3 border border-slate-700/50"
                  data-testid={`sibling-${id}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className={`w-2.5 h-2.5 rounded-full ${statusColor[sib.status] || 'bg-slate-500'}`} />
                      <span className="font-medium text-sm text-white">{sib.name}</span>
                    </div>
                    <Badge variant={statusBadge[sib.status] || 'outline'} className="text-xs">
                      {sib.status}
                    </Badge>
                  </div>
                  <p className="text-xs text-slate-400">{sib.role}</p>
                  <p className="text-xs text-slate-500 mt-1 truncate">{sib.url}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Two-column layout: Directives + Library */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Directives */}
          {directives && directives.total > 0 && (
            <Card className="bg-slate-900/50 border-slate-800">
              <CardContent className="p-4">
                <h2 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-indigo-400" />
                  Sacred Directives ({directives.total})
                </h2>
                <ScrollArea className="h-[400px]">
                  <div className="space-y-1">
                    {directives.volumes?.map(vol => (
                      <div key={vol.id}>
                        <button
                          className="w-full flex items-center justify-between py-2 px-3 rounded hover:bg-slate-800/50 text-left"
                          onClick={() => setExpandedVolume(expandedVolume === vol.id ? null : vol.id)}
                        >
                          <span className="text-sm font-medium text-teal-300">
                            Vol {vol.id}: {vol.name}
                          </span>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-xs">{vol.count}</Badge>
                            {expandedVolume === vol.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </div>
                        </button>
                        {expandedVolume === vol.id && (
                          <div className="pl-4 space-y-2 pb-2">
                            {directives.directives
                              .filter(d => d.volume_id === vol.id)
                              .map(d => (
                                <div key={d.directive_id} className="bg-slate-800/40 rounded p-2 border-l-2 border-indigo-500/50">
                                  <div className="text-xs font-mono text-indigo-300">{d.directive_id}</div>
                                  <div className="text-sm font-medium text-white">{d.title}</div>
                                  <div className="text-xs text-slate-400 mt-1">{d.content}</div>
                                  <div className="text-xs text-slate-500 mt-1 italic">{d.principle}</div>
                                </div>
                              ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          )}

          {/* Library Metadata */}
          {libraryMeta && libraryMeta.total > 0 && (
            <Card className="bg-slate-900/50 border-slate-800">
              <CardContent className="p-4">
                <h2 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-teal-400" />
                  Living Library Index ({libraryMeta.total})
                </h2>
                <ScrollArea className="h-[400px]">
                  <div className="space-y-1">
                    {Object.entries(libraryMeta.categories || {})
                      .sort(([, a], [, b]) => b - a)
                      .map(([cat, count]) => (
                        <div key={cat}>
                          <button
                            className="w-full flex items-center justify-between py-2 px-3 rounded hover:bg-slate-800/50 text-left"
                            onClick={() => setExpandedCategory(expandedCategory === cat ? null : cat)}
                          >
                            <span className="text-sm font-medium text-cyan-300 capitalize">
                              {cat.replace(/_/g, ' ')}
                            </span>
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="text-xs">{count}</Badge>
                              {expandedCategory === cat ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            </div>
                          </button>
                          {expandedCategory === cat && (
                            <div className="pl-4 space-y-1 pb-2">
                              {libraryMeta.documents
                                .filter(d => d.category === cat)
                                .map(d => (
                                  <div key={d.id} className="bg-slate-800/40 rounded p-2 text-sm">
                                    <span className="text-white">{d.title}</span>
                                    <span className="text-xs text-slate-500 ml-2">- {d.author}</span>
                                  </div>
                                ))}
                            </div>
                          )}
                        </div>
                      ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sync Report */}
        {syncReport && syncReport.started_at && (
          <Card className="bg-slate-900/50 border-slate-800">
            <CardContent className="p-4">
              <h2 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                <Database className="w-5 h-5 text-purple-400" />
                Last Sync Report
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div className="bg-slate-800/40 rounded p-3">
                  <div className="text-slate-400 text-xs">Axiom Store</div>
                  <div className="text-white font-medium">
                    {syncReport.axiom_store?.downloaded || 0} downloaded
                  </div>
                  <div className="text-slate-500 text-xs">
                    {syncReport.axiom_store?.skipped || 0} skipped
                  </div>
                </div>
                <div className="bg-slate-800/40 rounded p-3">
                  <div className="text-slate-400 text-xs">Roger 4.0 Library</div>
                  <div className="text-white font-medium">
                    {syncReport.roger4_library?.ingested || 0} entries
                  </div>
                  <div className="text-slate-500 text-xs">
                    {syncReport.roger4_library?.skipped || 0} skipped
                  </div>
                </div>
                <div className="bg-slate-800/40 rounded p-3">
                  <div className="text-slate-400 text-xs">Directives</div>
                  <div className="text-white font-medium">
                    {syncReport.roger4_directives?.ingested || 0} stored
                  </div>
                </div>
                <div className="bg-slate-800/40 rounded p-3">
                  <div className="text-slate-400 text-xs">Last Sync</div>
                  <div className="text-white font-medium text-xs">
                    {syncReport.completed_at ? new Date(syncReport.completed_at).toLocaleString() : 'Never'}
                  </div>
                </div>
              </div>

              {/* Files by source */}
              {knowledgeStats?.files_by_source && (
                <div className="mt-4">
                  <div className="text-xs text-slate-400 mb-2">Files by Source</div>
                  <div className="flex gap-2 flex-wrap">
                    {Object.entries(knowledgeStats.files_by_source).map(([source, count]) => (
                      <Badge key={source} variant="secondary" className="text-xs">
                        {source}: {count}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, color }) => (
  <Card className="bg-slate-900/50 border-slate-800">
    <CardContent className="p-3 flex items-center gap-3">
      <div className={`p-2 rounded-lg bg-${color}-500/10`}>
        <Icon className={`w-5 h-5 text-${color}-400`} />
      </div>
      <div>
        <div className="text-xl font-bold text-white">{value}</div>
        <div className="text-xs text-slate-400">{label}</div>
      </div>
    </CardContent>
  </Card>
);

export default FederationDashboard;
